import {
    Box,
    Card,
    Flex,
    Grid,
    HStack,
    Skeleton,
    SkeletonCircle,
    VStack,
} from "@chakra-ui/react";

const skeletonProps = {
    bg: "#DAE9FF",
    borderRadius: "md",
};

const cardProps = {
    bg: "white",
    border: "none",
    borderWidth: 0,
    boxShadow: "sm",
    borderRadius: "2xl",
};

const StatCard = () => (
    <Card.Root {...cardProps}>
        <Card.Body
            p={{
                base: 4,
                md: 6,
            }}
        >
            <Flex
                justify="space-between"
                align="center"
            >
                <Box flex="1">
                    <Skeleton
                        {...skeletonProps}
                        h="12px"
                        w="110px"
                        mb={3}
                    />

                    <Skeleton
                        {...skeletonProps}
                        h="30px"
                        w="80px"
                        mb={3}
                    />

                    <Skeleton
                        {...skeletonProps}
                        h="10px"
                        w="130px"
                    />
                </Box>

                <SkeletonCircle
                    bg="#DAE9FF"
                    size={{
                        base: "10",
                        md: "12",
                    }}
                />
            </Flex>
        </Card.Body>
    </Card.Root>
);

const LargeCard = ({ height }) => (
    <Card.Root {...cardProps}>
        <Card.Body
            p={{
                base: 4,
                md: 6,
            }}
        >
            <Skeleton
                {...skeletonProps}
                h="22px"
                w={{
                    base: "160px",
                    md: "220px",
                }}
                mb={6}
            />

            <Skeleton
                {...skeletonProps}
                h={{
                    base:
                        typeof height === "string"
                            ? "220px"
                            : height,
                    md: height,
                }}
                w="100%"
            />
        </Card.Body>
    </Card.Root>
);

const TableCard = () => (
    <Card.Root {...cardProps}>
        <Card.Body
            p={{
                base: 4,
                md: 6,
            }}
        >
            <Skeleton
                {...skeletonProps}
                h="22px"
                w={{
                    base: "170px",
                    md: "220px",
                }}
                mb={6}
            />

            <VStack
                align="stretch"
                gap={4}
            >
                {Array.from({
                    length: 8,
                }).map((_, i) => (
                    <Flex
                        key={i}
                        direction={{
                            base: "column",
                            md: "row",
                        }}
                        justify="space-between"
                        align={{
                            base: "flex-start",
                            md: "center",
                        }}
                        gap={{
                            base: 4,
                            md: 0,
                        }}
                    >
                        <HStack
                            flex={2}
                            w={{
                                base: "100%",
                                md: "auto",
                            }}
                        >
                            <SkeletonCircle
                                bg="#DAE9FF"
                                size="10"
                            />

                            <Box flex="1">
                                <Skeleton
                                    {...skeletonProps}
                                    h="12px"
                                    w="140px"
                                    mb={2}
                                />

                                <Skeleton
                                    {...skeletonProps}
                                    h="10px"
                                    w="90px"
                                />
                            </Box>
                        </HStack>

                        <Flex
                            w={{
                                base: "100%",
                                md: "auto",
                            }}
                            justify={{
                                base: "space-between",
                                md: "flex-end",
                            }}
                            gap={4}
                            wrap="wrap"
                            align="center"
                        >
                            <Skeleton
                                {...skeletonProps}
                                h="12px"
                                w="90px"
                            />

                            <Skeleton
                                {...skeletonProps}
                                h="12px"
                                w="80px"
                            />

                            <Skeleton
                                {...skeletonProps}
                                h="26px"
                                w="80px"
                                borderRadius="full"
                            />
                        </Flex>
                    </Flex>
                ))}
            </VStack>
        </Card.Body>
    </Card.Root>
);

export default function AnalyticsSkeleton() {
    return (
        <>
            {/* Header */}

            <Box
                mb={{
                    base: 6,
                    md: 8,
                }}
            >
                <Skeleton
                    {...skeletonProps}
                    h="36px"
                    w={{
                        base: "180px",
                        md: "260px",
                    }}
                    mb={3}
                />

                <Skeleton
                    {...skeletonProps}
                    h="16px"
                    w={{
                        base: "100%",
                        md: "500px",
                    }}
                />
            </Box>

            {/* Stats */}

            <Grid
                templateColumns={{
                    base: "1fr",
                    sm: "repeat(2,1fr)",
                    xl: "repeat(4,1fr)",
                }}
                gap={{
                    base: 4,
                    md: 6,
                }}
                mb={{
                    base: 6,
                    md: 8,
                }}
            >
                {Array.from({
                    length: 4,
                }).map((_, i) => (
                    <StatCard key={i} />
                ))}
            </Grid>

            {/* Growth Chart */}

            <LargeCard height="320px" />

            {/* Funnel + Commission */}

            <Grid
                mt={{
                    base: 4,
                    md: 6,
                }}
                gap={{
                    base: 4,
                    md: 6,
                }}
                templateColumns={{
                    base: "1fr",
                    lg: "1fr 1fr",
                }}
            >
                <LargeCard height="280px" />

                <LargeCard height="280px" />
            </Grid>

            {/* Driver Locations */}

            <Box
                mt={{
                    base: 4,
                    md: 6,
                }}
            >
                <LargeCard height="300px" />
            </Box>

            {/* Top Recruits */}

            <Box
                mt={{
                    base: 4,
                    md: 6,
                }}
            >
                <TableCard />
            </Box>
        </>
    );
}